import { abbrNumber } from '@hotjar/insights-common/lib/number';
import { useAuthorizationWithUserId } from '@hotjar/auth';
import { useCurrentUser } from '@hotjar/user';

import type { QuantzByDateResponse } from '@hotjar/quantz';

import { MiniWidget } from './MiniWidget';
import { useGetSessionErrors } from '../../../../hooks/useGetSessionErrors';
import { useGetErrorCapturingConsent } from '../../../../hooks/useGetErrorCapturingConsent';

import { SessionErrorsSegmentEmptyState } from './SessionErrorsSegmentEmptyState';
import type { SessionErrorsDataProps } from '../../../../constants-types/SessionErrors';

const getWidgetValue = (total?: number): string => {
  if (total !== undefined) {
    return abbrNumber(total) as string;
  }

  return '';
};

export function SessionErrorsSegmentData({
  selectedDateRange,
  selectedTargetingValue,
  selectedDeviceType,
  searchPattern,
  currentSite,
  selectedFiltersInUiFormat,
}: SessionErrorsDataProps): JSX.Element {
  const siteId = currentSite.id;
  const { isLoading, isError, isEmpty, data } = useGetSessionErrors({
    selectedDateRange,
    selectedTargetingValue,
    selectedDeviceType,
    searchPattern,
    siteId,
    selectedFiltersInUiFormat,
  });
  const sessionCaptureConsoleConsent = useGetErrorCapturingConsent(siteId, isEmpty);
  const userData = useCurrentUser().data;
  const { hasPermission } = useAuthorizationWithUserId(userData?.id);

  const hasSessionSettingsUpdatePermission = hasPermission(
    'site',
    `${siteId}`,
    'session-settings-update',
  );

  if (isEmpty && !sessionCaptureConsoleConsent) {
    return (
      <SessionErrorsSegmentEmptyState
        hasSessionSettingsUpdatePermission={hasSessionSettingsUpdatePermission}
        siteId={siteId}
      />
    );
  }

  return (
    <MiniWidget
      value={getWidgetValue(data?.data.total)}
      lineChartData={(data as QuantzByDateResponse)?.data.resultSeries?.[0].values}
      isLoading={isLoading}
      isError={isError}
      isEmpty={isEmpty}
      selectedTargetingValue={selectedTargetingValue}
      selectedDeviceType={selectedDeviceType}
      searchPattern={searchPattern}
      currentSite={currentSite}
      selectedFiltersInUiFormat={selectedFiltersInUiFormat}
    />
  );
}
